import { useEffect } from 'react';
import { App, Form, Input, Modal, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { plansQueryKeys, useCreatePlan, useUpdatePlan } from '@/features/agreements/hooks/usePlans';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';

const { Text, Paragraph } = Typography;

export const PlanFormModal = ({ open, onClose, agreementId, plan }) => {
  const [form] = Form.useForm();
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const createPlanMutation = useCreatePlan();
  const updatePlanMutation = useUpdatePlan();

  const isUpdate = !!plan;

  const handleOk = async () => {
    const values = await form.validateFields();

    if (isUpdate) {
      await updatePlanMutation.mutateAsync({ agreementId, planId: plan.id, data: values },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: plansQueryKeys.list({ agreementId }) });

            notification.success({
              message: <Text strong>Plan actualizado</Text>,
              description: '¡El plan se ha actualizado correctamente!',
            });
            onClose();
          },
          onError: (error) => {
            notification.error({
              message: <Text strong>Plan no actualizado</Text>,
              description: getErrorMessage(error),
            });
          },
        }
      );
    } else {
      await createPlanMutation.mutateAsync({ agreementId, data: values },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: plansQueryKeys.list({ agreementId }) });

            notification.success({
              message: <Text strong>Plan creado</Text>,
              description: '¡El plan se ha creado correctamente!',
            });
            onClose();
          },
          onError: (error) => {
            notification.error({
              message: <Text strong>Plan no creado</Text>,
              description: getErrorMessage(error),
            });
          },
        }
      );
    }
  };

  const isLoading = createPlanMutation.isPending || updatePlanMutation.isPending;

  // Inicializa el formulario con los datos del plan a actualizar
  useEffect(() => {
    if (open && isUpdate) {
      form.setFieldsValue({ name: plan.name });
    }
  }, [open, plan, form]);

  return (
    <Modal
      title={isUpdate ? 'Editar plan' : 'Nuevo plan'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      maskClosable={false}
      okText={isUpdate ? 'Guardar' : 'Crear plan'}
      confirmLoading={isLoading}
      destroyOnHidden
    >
      <Paragraph>
        {isUpdate ? 'Ingresa el nuevo nombre del plan.' : 'Define un nuevo plan para el convenio.'}
      </Paragraph>
      <Form form={form} layout='vertical' disabled={isLoading}>
        <Form.Item
          label='Nombre del plan'
          name='name'
          rules={[{ required: true }]}
        >
          <Input placeholder='Plan básico' />
        </Form.Item>
      </Form>
    </Modal>
  );
};
